import {
  ActionIcon,
  Box,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { type UseFieldReturnType, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconArrowDown, IconArrowRight, IconRocket, IconTrash } from '@tabler/icons-react';
import { useGetWorkflows } from 'api/index';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { AnimatePresence, motion } from 'motion/react';
import type React from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { filter, isDefined, isNullish, isNumber, map, pipe, prop, sum, times } from 'remeda';
import { useCreatePaymentPlan } from 'shared/hooks/use-create-payment-plan';
import { useLoadLightDebtors } from 'shared/hooks/use-load-light-debtors';
import type { PaymentPlanResources } from 'shared/hooks/use-load-payment-plan-resources';
import {
  getCurrencySymbolFromCurrencyCode,
  useLocalizedCurrencyFormatter,
} from 'shared/utils/currency';

import type { CalculatedSteps } from '../hooks';
import { adjustNextAmounts, adjustNextDates } from '../PaymentPlan.utils';
import type { InstallmentType } from '../PaymentPlanWizard';

import { RecalculateDatesModal } from './RecalculateDatesModal';

type Step4Props = {
  debtorField: UseFieldReturnType<string | null>;
  delay: UseFieldReturnType<number | string>;
  firstDeadlineDate: UseFieldReturnType<Date>;
  installmentType: UseFieldReturnType<InstallmentType>;
  installmentValue: UseFieldReturnType<number | string>;
  isPaymentStepsLoading: boolean;
  lateFees: UseFieldReturnType<boolean, 'checkbox'>;
  onVisit: VoidFunction;
  paymentSteps: CalculatedSteps | undefined;
  resources: PaymentPlanResources | undefined;
  selectedAccountingPayments: string[];
  selectedCreditNotes: string[];
  selectedInvoices: string[];
  selectedThirdPartyPayments: string[];
  workflowField: UseFieldReturnType<string | null>;
};

export const Step4 = ({
  debtorField,
  delay,
  firstDeadlineDate,
  isPaymentStepsLoading,
  lateFees,
  onVisit,
  paymentSteps,
  resources,
  selectedAccountingPayments,
  selectedCreditNotes,
  selectedInvoices,
  selectedThirdPartyPayments,
  workflowField,
}: Step4Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { lightDebtors: debtors } = useLoadLightDebtors({ query: '' });
  const { data: workflowsResponse } = useGetWorkflows();
  const company = useCompanyConfiguration();
  const { createPaymentPlan, isCreatePaymentPlanLoading } = useCreatePaymentPlan();
  const canUseAdvancedPaymentPlans = company.package.can_use_advanced_payment_plans;

  useEffect(() => onVisit(), []);

  const currency = resources?.invoices[0]?.amounts.debtorCurrency.currency ?? 'EUR';
  const formatter = useLocalizedCurrencyFormatter(false, currency);

  const customerName = debtors.find(debtor => debtor.id === debtorField.getValue())?.fullName;
  const workflowName =
    workflowsResponse?.data.find(workflow => workflow.id === workflowField.getValue())?.attributes
      .name ?? '';

  const paymentStepsForm = useForm({ initialValues: { steps: paymentSteps?.paymentSteps ?? [] } });
  const currentTotal = pipe(
    paymentStepsForm.getValues().steps,
    map(prop('amount')),
    filter(isNumber),
    sum(),
    fixed,
  );
  const requiredTotal = fixed(paymentSteps?.totalAmount ?? 0);
  const totalDelta = fixed(currentTotal - requiredTotal);
  const { steps } = paymentStepsForm.getValues();

  const promptDeadlineAdjustment = (index: number) => {
    if (index === steps.length - 1) return;
    modals.open({
      title: t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.ADJUST_DATES),
      size: 'auto',
      centered: true,
      onClose: () => {
        // Hack: Without this code, the DateInput's popover **re-opens** after the modal closes. What?
        const el = document.querySelector(`[data-findme="${index}"]`);
        setTimeout(() => {
          (el as any).blur();
        }, 60);
      },
      children: (
        <RecalculateDatesModal
          initialDelay={Number(delay.getValue()) ?? 30}
          onConfirm={newDelay => {
            paymentStepsForm.setValues(({ steps: _steps }) => ({
              steps: adjustNextDates(_steps!, index, newDelay),
            }));
            const el = document.querySelector(`[data-findme="${index}"]`);
            setTimeout(() => {
              (el as any).blur();
            }, 60);
          }}
        />
      ),
    });
  };

  const handleCreatePaymentPlan = async () => {
    createPaymentPlan(
      {
        debtorId: debtorField.getValue()!,
        workflowId: workflowField.getValue()!,
        withFees: lateFees.getValue(),
        invoiceIds: selectedInvoices,
        creditNoteIds: selectedCreditNotes,
        thirdPartyPaymentIds: selectedThirdPartyPayments,
        accountingPaymentIds: selectedAccountingPayments,
        paymentStepsAttributes: paymentStepsForm.getValues().steps.map(({ amount, dueDate }) => ({
          amount,
          dueDate: dayjs(dueDate).format('YYYY-MM-DD'),
        })),
      },
      {
        onSuccess: ({ paymentPlanId }) => history.push(`/payment-plans/${paymentPlanId}`),
      },
    );
  };

  return (
    <>
      <Group align="start">
        <Stack w="15%" miw={220}>
          <Title order={5} mb="xs">
            {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.OVERVIEW)}
          </Title>
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.CLIENT)}
            value={<Text>{customerName}</Text>}
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.WORKFLOW)}
            value={<Text>{workflowName}</Text>}
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.ITEMS_INCLUDED)}
            value={
              <>
                <Text>
                  {selectedInvoices.length}{' '}
                  {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.INVOICES, {
                    count: selectedInvoices.length,
                  })}
                </Text>
                {selectedCreditNotes.length > 0 && (
                  <Text>
                    {selectedCreditNotes.length}{' '}
                    {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.CREDIT_NOTES, {
                      count: selectedCreditNotes.length,
                    })}
                  </Text>
                )}
                {selectedThirdPartyPayments.length + selectedAccountingPayments.length > 0 && (
                  <Text>
                    {selectedThirdPartyPayments.length + selectedAccountingPayments.length}{' '}
                    {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.PAYMENTS, {
                      count: selectedThirdPartyPayments.length + selectedAccountingPayments.length,
                    })}
                  </Text>
                )}
              </>
            }
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.DEADLINE_DELAY)}
            value={`${delay.getValue()}${t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.DAYS_SUFFIX)}`}
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.FIRST_DEADLINE)}
            value={dayjs(firstDeadlineDate.getValue()).format('LL')}
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.WITH_LATE_FEES)}
            value={lateFees.getValue() ? t(i18nKeys.YES) : t(i18nKeys.NO)}
          />
          <RecapItem
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.TOTAL)}
            value={
              isDefined(paymentSteps) ? formatter.format(paymentSteps.totalAmount) : <Skeleton />
            }
          />
        </Stack>
        <Divider orientation="vertical" mx="lg" />
        <Stack flex={1}>
          <Title order={5} mb="xs">
            {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEPS.INSTALLMENTS)}
          </Title>
          <Card radius="md" p={0}>
            <Table
              verticalSpacing="xs"
              style={{ borderRadius: 6 }}
              highlightOnHover
              withColumnBorders
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t(i18nKeys.DATE)}</Table.Th>
                  <Table.Th>{t(i18nKeys.AMOUNT)}</Table.Th>
                  {canUseAdvancedPaymentPlans && <Table.Th />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isPaymentStepsLoading ? (
                  times(12, () => (
                    <Table.Tr key={Math.random()}>
                      <Table.Td>
                        <Skeleton height={30} w={170} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={30} w={170} />
                      </Table.Td>
                      {canUseAdvancedPaymentPlans && (
                        <Table.Td>
                          <Group>
                            <Skeleton height={30} w={30} />
                            <Skeleton height={30} w={100} />
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))
                ) : (
                  <>
                    {paymentStepsForm.getValues().steps.map(({ id, dueDate, amount }, index) => {
                      const previousStep =
                        paymentStepsForm.getValues().steps[Math.max(index - 1, 0)];
                      return (
                        <Table.Tr key={id}>
                          <Table.Td>
                            {canUseAdvancedPaymentPlans ? (
                              <DateInput
                                data-findme={index}
                                w={170}
                                size="xs"
                                minDate={previousStep.dueDate}
                                {...paymentStepsForm.getInputProps(`steps.${index}.dueDate`)}
                                onChange={date => {
                                  if (isNullish(date)) return;
                                  paymentStepsForm.setFieldValue(
                                    `steps.${index}.dueDate`,
                                    new Date(date),
                                  );
                                  promptDeadlineAdjustment(index);
                                }}
                              />
                            ) : (
                              dayjs(dueDate).format('LL')
                            )}
                          </Table.Td>
                          {/* Fixed width required to prevent the column from changing size when
                        we toggle on the text in the last row */}
                          <Table.Td w="40%">
                            {canUseAdvancedPaymentPlans ? (
                              <NumberInput
                                w={170}
                                hideControls
                                leftSection={`${getCurrencySymbolFromCurrencyCode(currency)} `}
                                {...paymentStepsForm.getInputProps(`steps.${index}.amount`)}
                                size="xs"
                              />
                            ) : (
                              formatter.format(amount)
                            )}
                          </Table.Td>
                          {canUseAdvancedPaymentPlans && (
                            <Table.Td>
                              <Group>
                                <ActionIcon
                                  onClick={() => paymentStepsForm.removeListItem('steps', index)}
                                  size="input-xs"
                                  variant="light"
                                  color="red"
                                >
                                  <IconTrash stroke={1.5} size={16} />
                                </ActionIcon>
                                <Tooltip
                                  disabled={totalDelta === 0}
                                  w={300}
                                  multiline
                                  label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.ADJUST_TOOLTIP)}
                                >
                                  <Button
                                    disabled={totalDelta === 0}
                                    size="compact-md"
                                    variant="light"
                                    fz={14}
                                    rightSection={<IconArrowDown stroke={1.5} size={16} />}
                                    onClick={() =>
                                      paymentStepsForm.setFieldValue(
                                        'steps',
                                        adjustNextAmounts(
                                          paymentStepsForm.getValues().steps,
                                          index,
                                          requiredTotal,
                                        ),
                                      )
                                    }
                                  >
                                    {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.ADJUST)}
                                  </Button>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      );
                    })}
                    <Table.Tr>
                      <Table.Td />
                      <Table.Td>
                        <Box w="50%">
                          <Text fw="bold" c={currentTotal === requiredTotal ? 'green' : 'red'}>
                            {formatter.format(currentTotal)}
                          </Text>
                          <AnimatePresence>
                            {totalDelta !== 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                              >
                                <Text c="dimmed">
                                  {`${formatter.format(Math.abs(totalDelta))} ${totalDelta > 0 ? t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.OVER) : t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.UNDER)}`}{' '}
                                  {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.TOTAL_MESSAGE)}
                                </Text>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Box>
                      </Table.Td>
                      {canUseAdvancedPaymentPlans && <Table.Td />}
                    </Table.Tr>
                  </>
                )}
              </Table.Tbody>
            </Table>
          </Card>
        </Stack>
      </Group>

      <Group justify="end" mt="lg" flex={1}>
        <Stack>
          <Button
            size="md"
            my="md"
            disabled={requiredTotal !== currentTotal}
            rightSection={<IconArrowRight stroke={1.5} size={20} />}
            leftSection={<IconRocket stroke={1.5} size={20} />}
            onClick={handleCreatePaymentPlan}
            loading={isCreatePaymentPlanLoading}
          >
            {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.LAUNCH)}
          </Button>
        </Stack>
      </Group>
    </>
  );
};

type RecapItemProps = { label: string; value: React.ReactNode };

const RecapItem = ({ label, value }: RecapItemProps) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label.toUpperCase()}
    </Text>
    {value}
  </Stack>
);

function fixed(n: number) {
  return Math.round(n * 100) / 100;
}
